import { PayloadAction } from '@reduxjs/toolkit';
import _isUndefined from 'lodash/isUndefined';
import _omitBy from 'lodash/omitBy';
import { call, put } from 'redux-saga/effects';
import {
    select,
    all as typedAll,
    call as typedCall,
    put as typedPut,
    select as typedSelect,
    take as typedTake,
} from 'typed-redux-saga';

import { ErrorPageType } from '../../../../../shared/components/error-page/types';
import { SELF } from '../../../../../shared/constants';
import { setConferenceMediaPlatform, setErrorPageAction } from '../../../../../shared/features/config';
import { getAppVersionSelector, selectIsGoloomRc } from '../../../../../shared/features/config/selectors';
import { getUserSelector, toPassport } from '../../../../../shared/features/user';
import { FetchError } from '../../../../../shared/helpers/errors';
import { i18n } from '../../../../../shared/helpers/i18n';
import { ACTION, FEATURE } from '../../../../../shared/helpers/logger-enums';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { countMetrika } from '../../../../../shared/helpers/metrika';
import { getUrl, navigate } from '../../../../../shared/helpers/navigation';
import { replace } from '../../../../../shared/helpers/navigation/index';
import { SCREENS } from '../../../../../shared/helpers/navigation/routing';
import { ONE_TIME_KEYS, setOneTimeValue } from '../../../../../shared/helpers/one-time-value';
import { cloudApi } from '../../../../../shared/helpers/transport';
import { ConferenceStatus } from '../../../../call/constants';
import {
    disconnectedFromConferenceDoneAction,
    setJoinedAsGuest,
    setRoomConferenceConfig,
    updateParticipant,
} from '../../../../call/features/conference/slice';
import { normalizeConferenceUri } from '../../../../call/helpers/conference-config';
import {
    getConferenceId,
    getConferenceStatus,
    selectIsBroadcastEnabled,
    selectIsConferenceB2BDomainMismatch,
    selectYaServices,
} from '../../../../call/selectors';
import { ConferenceConfig, ParticipantState } from '../../../../call/types';
import { PlanBroadcastAction } from '../slice';

interface CreateCallSagaParams {
    access_level?: string;
}

const logger = createLogger('create-call');

function* handleCreateCallError(error: unknown): Generator {
    logger.error(error, {
        method: 'createCallSaga',
    });

    let shouldShowDefaultError = true;

    if (FetchError.is(error)) {
        logger.endFeatureDelta({
            feature: FEATURE.CONNECTION,
            action: ACTION.CREATE_CONFERENCE,
            isSuccess: false,
            options: {
                additional: {
                    status: error.status,
                    error: error.error,
                },
            },
        });

        switch (error.status) {
            case 403:
                yield* typedPut(setErrorPageAction({ type: ErrorPageType.NOT_IN_EXPERIMENT }));
                shouldShowDefaultError = false;
                break;
            case 503:
                if (error.error === 'TelemostServiceTemporaryOverloaded') {
                    yield* typedPut(setErrorPageAction({ type: ErrorPageType.OVERLOADED }));
                    shouldShowDefaultError = false;
                }
                break;
            case 401:
                yield* typedPut(
                    toPassport({
                        retpath: '/browser-auto-create',
                        backpath: '/',
                    })
                );
                shouldShowDefaultError = false;
                break;
        }
    } else {
        logger.endFeatureDelta({
            feature: FEATURE.CONNECTION,
            action: ACTION.CREATE_CONFERENCE,
            isSuccess: false,
        });
    }

    if (shouldShowDefaultError) {
        yield* typedPut(
            setErrorPageAction({
                type: ErrorPageType.DEFAULT,
                reason: {
                    method: 'createCallSaga',
                    message: error instanceof Error ? error.message : 'Caught error creating call',
                },
            })
        );
    }
}

/**
 *
 */
export function* createCallSaga(params?: CreateCallSagaParams): Generator {
    try {
        const status = yield* typedSelect(getConferenceStatus);
        if (status !== ConferenceStatus.DISCONNECTED) {
            yield* typedTake(disconnectedFromConferenceDoneAction);
        }

        const user = yield* typedSelect(getUserSelector);

        if (user?.accessToken) {
            cloudApi.setApiAuthorizationHeader(user.accessToken);
        }
        const isGoloomRc = yield* select(selectIsGoloomRc);

        yield put(setJoinedAsGuest(false));

        const appVersion = yield* select(getAppVersionSelector);

        const { conference, me } = yield* typedAll({
            conference: typedCall(
                [cloudApi, cloudApi.createConference],
                { access_level: params?.access_level },
                true,
                appVersion,
                isGoloomRc
            ),
            me: user?.uid ? typedCall([cloudApi, cloudApi.getMeUser]) : typedCall(() => undefined),
        });

        // eslint-disable-next-line no-console
        console.log('createCall config', conference);

        const updateData: Partial<ParticipantState> = {
            avatarUrl: me?.avatar_url as string | undefined,
            isDefaultAvatar: me?.is_default_avatar as boolean | undefined,
            displayName: me?.display_name,
            avatarPlaceholder: me?.avatar_placeholder
                ? {
                      abbreviation: me.avatar_placeholder.abbreviation,
                      backgroundColor: me.avatar_placeholder.background_color,
                      textColor: me.avatar_placeholder.text_color,
                  }
                : undefined,
        };

        yield* typedPut(
            updateParticipant({
                id: SELF,
                ...updateData,
            })
        );

        const config: ConferenceConfig = {
            ...conference,
            display_name: me?.display_name || i18n('common', 'guest'),
            resolved_uri: normalizeConferenceUri(conference.uri),
        };

        yield put(setRoomConferenceConfig(config));
        const id = yield* typedSelect(getConferenceId);
        if (id === '') {
            throw new Error('conf id is not valid');
        }

        yield put(setConferenceMediaPlatform(conference.media_platform));

        countMetrika('main_screen', 'create_meeting', conference.room_id);
        logger.endFeatureDelta({
            feature: FEATURE.CONNECTION,
            action: ACTION.CREATE_CONFERENCE,
            isSuccess: true,
        });

        const isDomainMismatch = yield* typedSelect(selectIsConferenceB2BDomainMismatch);
        if (isDomainMismatch) {
            setOneTimeValue(ONE_TIME_KEYS.ID_CREATED_WITH_RELOAD, id);
            window.location.href = getUrl(SCREENS.CALL, { id, searchString: window.location.search });
        } else {
            yield call(navigate, SCREENS.CALL, { id });
        }
    } catch (error) {
        yield* handleCreateCallError(error);
    }
}

/**
 *
 */
export function* connectCallSaga(): Generator {
    yield* typedCall(replace, SCREENS.CONNECT_CALL, {});
}

/**
 *
 */
function* redirectToTariffs() {
    const { yandex360 } = yield* typedSelect(selectYaServices);
    window.open(`${yandex360}/business/tariff/`);
}

/**
 * @param {SCREENS} retScreen
 */
function* redirectToPassport(retScreen: SCREENS) {
    const user = yield* typedSelect(getUserSelector);
    yield* typedPut(
        toPassport({
            retpath: getUrl(retScreen),
            backpath: window.location.pathname,
            url: user?.passportUpgradeUrl,
        })
    );
}

/**
 *
 */
export function* planCallSaga(): Generator {
    const user = yield* typedSelect(getUserSelector);

    if (user && !user.passportUpgradeUrl) {
        yield* typedCall(navigate, SCREENS.PLAN_CALL);
    } else {
        yield* typedCall(redirectToPassport, SCREENS.PLAN_CALL);
    }
}

/**
 *
 */
export function* planBroadcastSaga({ payload }: PayloadAction<PlanBroadcastAction>): Generator {
    const user = yield* typedSelect(getUserSelector);
    if (user) {
        if (user.broadcastFeatureEnabled) {
            yield* typedCall(navigate, SCREENS.PLAN_BROADCAST, _omitBy(payload, _isUndefined));
        } else {
            yield* typedCall(redirectToTariffs);
        }
    } else {
        yield* typedCall(redirectToPassport, SCREENS.PLAN_BROADCAST);
    }
}

/**
 *
 */
export function* createBroadcastFormSaga(): Generator {
    const shouldCreate = yield* typedSelect(selectIsBroadcastEnabled);
    if (!shouldCreate) {
        yield* typedCall(redirectToTariffs);
    } else {
        yield* typedCall(replace, SCREENS.CREATE_BROADCAST);
    }
}
